import { supabase } from '@/lib/supabase/client';
import type { DisplayComponent, DisplayComponentInsert, DisplayComponentUpdate } from '@/types/display-components';

export const displayComponentsService = {
  async getByDisplayTypeId(displayTypeId: string): Promise<DisplayComponent[]> {
    try {
      const { data, error } = await supabase
        .from('display_components')
        .select('*')
        .eq('display_type_id', displayTypeId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching display components:', error);
        return [];
      }

      return (data as DisplayComponent[]) || [];
    } catch (error) {
      console.error('Error fetching display components:', error);
      return [];
    }
  },

  async getByComponentId(componentId: string): Promise<DisplayComponent[]> {
    try {
      const { data, error } = await supabase
        .from('display_components')
        .select('*')
        .eq('component_id', componentId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching display components by component ID:', error);
        return [];
      }

      return (data as DisplayComponent[]) || [];
    } catch (error) {
      console.error('Error fetching display components by component ID:', error);
      return [];
    }
  },

  async create(data: DisplayComponentInsert): Promise<DisplayComponent> {
    try {
      const insertData = {
        display_type_id: data.display_type_id,
        component_id: data.component_id,
        quantity_required: data.quantity_required,
        notes: data.notes ?? null,
      };

      const { data: newDisplayComponent, error } = await supabase
        .from('display_components')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating display component:', error);
        throw new Error(`Failed to create display component: ${error.message}`);
      }

      return newDisplayComponent as DisplayComponent;
    } catch (error) {
      console.error('Error creating display component:', error);
      throw error;
    }
  },

  async update(id: string, data: DisplayComponentUpdate): Promise<DisplayComponent> {
    try {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (data.display_type_id !== undefined) updateData.display_type_id = data.display_type_id;
      if (data.component_id !== undefined) updateData.component_id = data.component_id;
      if (data.quantity_required !== undefined) updateData.quantity_required = data.quantity_required;
      if (data.notes !== undefined) updateData.notes = data.notes;

      const { data: updatedDisplayComponent, error } = await supabase
        .from('display_components')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating display component:', error);
        throw new Error(`Failed to update display component: ${error.message}`);
      }

      if (!updatedDisplayComponent) {
        throw new Error('Display component not found');
      }

      return updatedDisplayComponent as DisplayComponent;
    } catch (error) {
      console.error('Error updating display component:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase.from('display_components').delete().eq('id', id);

      if (error) {
        console.error('Error deleting display component:', error);
        throw new Error(`Failed to delete display component: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting display component:', error);
      throw error;
    }
  },

  async deleteByDisplayTypeId(displayTypeId: string): Promise<void> {
    try {
      const { error } = await supabase.from('display_components').delete().eq('display_type_id', displayTypeId);

      if (error) {
        console.error('Error deleting display components by display type ID:', error);
        throw new Error(`Failed to delete display components: ${error.message}`);
      }
    } catch (error) {
      console.error('Error deleting display components by display type ID:', error);
      throw error;
    }
  },
};

